/**
 * HTTP client for the flow-web API (contract §1). `fetchJson` unwraps the
 * `{data, meta}` envelope, reports `meta.warnings` to the warnings store and
 * `meta.bucket_secs` to the UI store (what "auto" resolved to), throws
 * `ApiRequestError` on `{error}` bodies and redirects to /login on 401.
 * Pass `{ silent: true }` (prefetches, background neighbours) to skip both
 * reports.
 */
import { useUi } from "@/state/ui";
import { useWarnings } from "@/state/warnings";
import type { ApiError, ApiErrorBody, Envelope, ErrorCode, Meta } from "./types";

export type QueryValue = string | number | boolean | undefined | null | readonly (string | number)[];
export type QueryParams = Record<string, QueryValue>;

export const API_PREFIX = "/api";

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly details?: Record<string, unknown>;
  readonly path: string;

  constructor(path: string, status: number, body: ApiErrorBody) {
    super(body.message || `${status} on ${path}`);
    this.name = "ApiRequestError";
    this.path = path;
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }

  get isNotFound(): boolean {
    return this.code === "not_found";
  }
  get isUnavailable(): boolean {
    return this.code === "unavailable";
  }
  get isNoMark(): boolean {
    return this.code === "no_mark";
  }
}

export function isApiError(e: unknown): e is ApiRequestError {
  return e instanceof ApiRequestError;
}

/** Array params are comma-joined (`kind=buy,sell`), undefined/null/empty arrays are dropped. */
export function buildQuery(params?: QueryParams): string {
  if (!params) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      p.set(k, v.join(","));
    } else if (typeof v === "boolean") {
      p.set(k, v ? "true" : "false");
    } else {
      p.set(k, String(v));
    }
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export interface ApiResult<T> {
  data: T;
  meta: Meta;
}

export interface FetchOptions {
  /**
   * Do not report `meta.warnings` to the global warnings bar (nor
   * `meta.bucket_secs` to the bucket picker). For prefetches of ranges the
   * user is not looking at.
   */
  silent?: boolean;
}

let redirectingToLogin = false;

function redirectToLogin(): void {
  if (redirectingToLogin || typeof location === "undefined") return;
  if (location.pathname === "/login") return;
  redirectingToLogin = true;
  const next = encodeURIComponent(location.pathname + location.search);
  location.assign(`/login?next=${next}`);
}

async function parseError(path: string, res: Response): Promise<ApiRequestError> {
  let body: ApiErrorBody = { code: statusToCode(res.status), message: `${res.status} ${res.statusText}` };
  try {
    const json = (await res.json()) as Partial<ApiError>;
    if (json && typeof json === "object" && json.error && typeof json.error.message === "string")
      body = json.error;
  } catch {
    /* non-JSON error body */
  }
  return new ApiRequestError(path, res.status, body);
}

function statusToCode(status: number): ErrorCode {
  switch (status) {
    case 400:
      return "bad_request";
    case 401:
      return "unauthorized";
    case 404:
      return "not_found";
    case 409:
      return "conflict";
    case 503:
      return "unavailable";
    default:
      return "internal";
  }
}

async function request<T>(
  method: string,
  path: string,
  params?: QueryParams,
  body?: unknown,
  signal?: AbortSignal,
  opts: FetchOptions = {},
): Promise<ApiResult<T>> {
  const url = `${API_PREFIX}${path}${buildQuery(params)}`;
  const res = await fetch(url, {
    method,
    headers:
      body !== undefined
        ? { "content-type": "application/json", accept: "application/json" }
        : { accept: "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
    signal,
  });
  if (res.status === 401) {
    redirectToLogin();
    throw await parseError(path, res);
  }
  if (!res.ok) throw await parseError(path, res);
  if (res.status === 204) {
    return {
      data: undefined as T,
      meta: { generated_at: Date.now() / 1000, coverage: [], db_version: 0, warnings: [] },
    };
  }
  const env = (await res.json()) as Envelope<T>;
  const meta: Meta = env.meta ?? {
    generated_at: Date.now() / 1000,
    coverage: [],
    db_version: 0,
    warnings: [],
  };
  if (!opts.silent) {
    useWarnings.getState().report(path, meta.warnings ?? [], meta.notes ?? []);
    if (typeof meta.bucket_secs === "number") useUi.getState().setServerBucket(meta.bucket_secs);
  }
  return { data: env.data, meta };
}

/** GET `/api{path}?params` and unwrap the envelope. `opts.silent` skips the meta reports. */
export function fetchJson<T>(
  path: string,
  params?: QueryParams,
  signal?: AbortSignal,
  opts?: FetchOptions,
): Promise<ApiResult<T>> {
  return request<T>("GET", path, params, undefined, signal, opts);
}

/** `fetchJson` for prefetches: warnings / bucket of ranges the user is not viewing stay out of the UI. */
export function fetchJsonSilent<T>(
  path: string,
  params?: QueryParams,
  signal?: AbortSignal,
): Promise<ApiResult<T>> {
  return request<T>("GET", path, params, undefined, signal, { silent: true });
}

export function postJson<T>(path: string, body: unknown, params?: QueryParams): Promise<ApiResult<T>> {
  return request<T>("POST", path, params, body);
}

export function putJson<T>(path: string, body: unknown, params?: QueryParams): Promise<ApiResult<T>> {
  return request<T>("PUT", path, params, body);
}

export function deleteJson<T>(path: string, params?: QueryParams): Promise<ApiResult<T>> {
  return request<T>("DELETE", path, params);
}

/** POST /api/login {token} -> 204 + cookie. Resolves true on success. */
export async function login(token: string): Promise<boolean> {
  const res = await fetch(`${API_PREFIX}/login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
    credentials: "same-origin",
  });
  return res.status === 204 || res.ok;
}

function identityPath(suffix: string, chain: string, address: string, token?: string, scope?: string) {
  const q = new URLSearchParams();
  if (token) q.set("token", token);
  if (scope) q.set("scope", scope);
  const s = q.toString();
  return `/identity/${chain}/${address}${suffix}${s ? `?${s}` : ""}`;
}

/** Path helpers so slices never hand-build job paths. */
export const paths = {
  jobs: () => "/jobs",
  job: (chain: string, token: string) => `/jobs/${chain}/${token}`,
  liquidity: (chain: string) => `/liquidity/${chain}`,
  hooks: (chain: string) => `/hooks/${chain}`,
  identity: (chain: string, address: string, token?: string, scope?: string) =>
    identityPath("", chain, address, token, scope),
  /** the bookmark at the same scope `identity` reads and writes */
  identityStar: (chain: string, address: string, token?: string, scope?: string) =>
    identityPath("/star", chain, address, token, scope),
  /** the owned mark: every chain unless `scope` names a narrower row */
  identityOwned: (chain: string, address: string, token?: string, scope?: string) =>
    identityPath("/owned", chain, address, token, scope),
  search: () => "/search",
  notes: () => "/notes",
  views: () => "/views",
  signals: () => "/signals",
  health: () => "/health",
  events: () => `${API_PREFIX}/events`,
  // alerts slice
  rules: () => "/rules",
  rule: (id: number) => `/rules/${id}`,
  rulePreview: () => "/rules/preview",
  alerts: () => "/alerts",
  alertsSummary: () => "/alerts/summary",
  alertDeliveries: (id: number) => `/alerts/${id}/deliveries`,
  alertsRead: () => "/alerts/read",
  pushKey: () => "/push/key",
  pushSubscriptions: () => "/push/subscriptions",
  pushSubscribe: () => "/push/subscribe",
  pushUnsubscribe: () => "/push/unsubscribe",
  pushTest: () => "/push/test",
  // positions slice: the operator's own, every token
  myPositions: () => "/positions",
  myPositionsHistory: () => "/positions/history",
} as const;
